import type { TypographyVariantsOptions } from "@mui/material/styles";

/**
 * Material 3 type scale, mapped onto MUI v6's Typography variants.
 * Roboto Flex for display (expressive) + Inter for body/UI (legible on small widths).
 * Tabular numerics forced on anywhere currency values are likely.
 */

const FONT_DISPLAY = '"Roboto Flex Variable", "Roboto Flex", "Inter Variable", system-ui, sans-serif';
const FONT_SANS    = '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const FONT_MONO    = 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, Consolas, monospace';

export const typography: TypographyVariantsOptions = {
  fontFamily: FONT_SANS,

  // Display — hero numbers (portfolio balance)
  h1: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    fontSize: "clamp(2.25rem, 8vw, 3.5rem)",      // 36 → 56
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
    fontVariationSettings: '"wdth" 110'            // slightly wider — Roboto Flex special sauce
  },
  h2: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: 1.15,
    letterSpacing: "-0.015em"
  },
  h3: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: "1.5rem",
    lineHeight: 1.2,
    letterSpacing: "-0.01em"
  },
  h4: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: "1.25rem",
    lineHeight: 1.25
  },
  h5: {
    fontFamily: FONT_SANS,
    fontWeight: 600,
    fontSize: "1.0625rem",
    lineHeight: 1.3
  },
  h6: {
    fontFamily: FONT_SANS,
    fontWeight: 600,
    fontSize: "0.9375rem",
    lineHeight: 1.3
  },

  // Body
  body1: {
    fontFamily: FONT_SANS,
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: 1.5
  },
  body2: {
    fontFamily: FONT_SANS,
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: 1.5
  },
  caption: {
    fontFamily: FONT_SANS,
    fontWeight: 500,
    fontSize: "0.75rem",
    lineHeight: 1.4,
    letterSpacing: "0.02em"
  },

  // Labels
  subtitle1: {
    fontFamily: FONT_SANS,
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: 1.4
  },
  subtitle2: {
    fontFamily: FONT_SANS,
    fontWeight: 600,
    fontSize: "0.8125rem",
    lineHeight: 1.4,
    letterSpacing: "0.01em"
  },
  overline: {
    fontFamily: FONT_SANS,
    fontWeight: 700,
    fontSize: "0.6875rem",
    lineHeight: 1.4,
    letterSpacing: "0.12em",
    textTransform: "uppercase"
  },
  button: {
    fontFamily: FONT_SANS,
    fontWeight: 600,
    fontSize: "0.9375rem",
    lineHeight: 1.2,
    textTransform: "none",
    letterSpacing: "0.01em"
  }
};

/** Monospace family for tabular numerics (ticker symbols, prices in tables). */
export const TABULAR = {
  fontFamily: FONT_MONO,
  fontFeatureSettings: '"tnum" 1, "zero" 1'
};
